import React, { useCallback, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import { STEP, stepIcon } from './toolkit/ToolkitFileStep';
import ToolkitBatchTable from './toolkit/ToolkitBatchTable';
import ToolkitWriteConfirm, { confirmed } from './toolkit/ToolkitWriteConfirm';
import useToolkitBatch from './toolkit/useToolkitBatch';
import useOnPrem, { rowsOf } from './toolkit/useOnPrem';
import ToolkitResyncUdf, { useResyncUdf } from './toolkit/ToolkitResyncUdf';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › Profile Re-sync — the desktop's Scripts › Profiles. Re-sends the profiles of a date range
// from the property's OPERA v5 to Opera Cloud: each profile gets a free UDF stamped PROCESSED_<time> and is saved,
// which OPERA's OTA save sends through OXI to Cloud with its current data (toolkit onprem.js WRITES.resync-profiles).
//
//   1. Profiles — every guest, company, agent, source and group profile on stays departed in the range.
//   2. UDF — a character UDF no profile uses, worked out from the property's UDF usage; remembered per property.
//   3. Confirm — this WRITES to the live v5 database; the project name is typed to allow it.
//   4. Re-send — in batches of 200, one SyncConnect request each; a profile counts as re-sent only when its UDF reads
//      back with this run's stamp. In this tab, or on the server (Run on server), which waits for each answer itself.

const BATCH = 200;
const today = () => new Date().toISOString().slice(0, 10);
const COLUMNS = [
    { key: 'batch', label: 'Batch' },
    { key: 'count', label: 'Profiles' },
    { key: 'types', label: 'Types' },
    { key: 'result', label: 'Result' },
];

function ToolkitProfileResyncPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready, project } = toolkit;
    const { ask, write } = useOnPrem(ctx);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState(today());
    const [find, setFind] = useState({ state: STEP.IDLE, message: '' });
    const resync = useResyncUdf(ctx, ready);
    const { udf } = resync;
    const [confirm, setConfirm] = useState('');
    const ok = confirmed(project, confirm);

    const processRow = useCallback(async (row) => {
        const detail = await write('resync-profiles', { nameIds: row.ids, udfField: udf }, confirm, { ids: row.ids });
        if (detail.status === 'ERROR') return { status: 'failed', errorMessage: detail.error_message };
        if (detail.status !== 'RETURNED') return { status: 'failed', errorMessage: `No answer yet (${detail.status}) — the request is still queued; check the profiles before sending again` };
        const o = detail.writeOutcome || {};
        const resent = (o.items || []).filter((i) => i.resynced).length;
        return { status: o.status === 'success' ? 'success' : 'failed', result: `${resent} of ${row.count} re-sent`, errorMessage: o.message };
    }, [write, udf, confirm]);

    // On the server (Run on server — toolkit jobs.js): each batch is the same write, sent to SyncConnect (or issued as a
    // script to run by hand); the job waits for each answer itself, so the page can be closed.
    const request = useCallback((row) => ({ area: 'onprem', op: 'write', args: { write: 'resync-profiles', args: { nameIds: row.ids, udfField: udf } } }), [udf]);
    const interpret = useCallback((r, row) => {
        const resent = (r.outcome?.items || []).filter((i) => i.resynced).length;
        return { status: r.status, result: r.outcome ? `${resent} of ${row.count} re-sent` : row.result, errorMessage: r.errorMessage || null };
    }, []);
    const batch = useToolkitBatch({ processRow, request, interpret, ctx,
        server: { screen: 'profile-resync', title: 'Profile Re-sync', keyOf: (row) => `${row.batch}|${row.ids[0]}`, extra: () => ({ confirm }) } });

    const findProfiles = async () => {
        setFind({ state: STEP.BUSY, message: 'Asking SyncConnect for the profiles…' });
        try {
            const d = await ask('profiles-from-stays', { startDate: from, endDate: to });
            if (d.status !== 'RETURNED') { setFind({ state: STEP.ERROR, message: d.error_message || `No answer yet (${d.status}) — try again shortly.` }); return; }
            const rows = rowsOf(d.result).filter((r) => r.name_id);
            const batches = [];
            for (let i = 0; i < rows.length; i += BATCH) {
                const part = rows.slice(i, i + BATCH);
                const types = part.reduce((m, r) => { m[r.profile_type] = (m[r.profile_type] || 0) + 1; return m; }, {});
                batches.push({ batch: batches.length + 1, ids: part.map((r) => String(r.name_id)), count: part.length,
                    types: Object.entries(types).map(([k, v]) => `${k} ${v}`).join(', ') });
            }
            batch.load(batches);
            setFind({ state: STEP.DONE, message: `${rows.length} profile(s) in ${batches.length} batch(es)${d.result_truncated === 'Y' ? ' — the answer was cut short; narrow the dates' : ''}.` });
        } catch (e) {
            setFind({ state: STEP.ERROR, message: e?.response?.data?.message || e.message });
        }
    };

    const total = useMemo(() => batch.rows.reduce((n, r) => n + r.count, 0), [batch.rows]);

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="Profile Re-sync"
                subtitle="Re-send the profiles of a date range from Opera v5 to Opera Cloud, through OXI." />

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">1</span> Profiles on stays departed {stepIcon(find.state)}</div>
                <div className="tkb-row">
                    <label><span>From</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                    <label><span>To</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
                    <Button variant="secondary" onClick={findProfiles} disabled={!ready || !from || !to || from > to || find.state === STEP.BUSY || batch.running}>
                        <Search className="icon" /> Find profiles
                    </Button>
                </div>
                {find.message && <p className={`tkb-msg${find.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{find.message}</p>}
            </div>

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">2</span> UDF to stamp</div>
                <ToolkitResyncUdf resync={resync} disabled={!ready} />
            </div>

            {batch.rows.length > 0 && (
                <div className="tkb-step">
                    <div className="tkb-step-head"><span className="tkb-num">3</span> Confirm</div>
                    <ToolkitWriteConfirm project={project} value={confirm} onChange={setConfirm}
                        what={`${total} profile(s) will have ${udf || 'the chosen UDF'} set to PROCESSED_<time> and be saved, which re-sends each one through OXI to Opera Cloud.`} />
                </div>
            )}

            <ToolkitBatchTable batch={batch} columns={COLUMNS} searchKeys={['batch', 'types']} exportName="Profile_Resync"
                canRun={ready && !!udf && ok} onClear={() => batch.load([])} />
        </div>
    );
}

export default ToolkitProfileResyncPage;
